import {useCallback, useEffect, useMemo} from 'react';
import {
    applyOccasionFilter,
    fetchOccasions,
    filterOccasions,
    loadOccasionsFromDatabase,
} from '../../store/slices/occasions_slice';
import {useAppDispatch, useAppSelector} from '../../store/store';
import {Status} from '../../enums/status';
import {Outlet, useSearchParams} from 'react-router-dom';
import {parseIntOrFallback} from '../../support/parse_int';
import {useIsNavigatingOrPending} from '../../support/use_is_navigating_or_pending';
import {Filters, OccasionFilters} from './components/filters';
import {CenteredSpinner} from '../../components/spinner/centered_spinner';
import {Button} from '../../components/button/button.tsx';
import {Main} from '../../components/main/main.tsx';
import {OccasionCard} from './components/occasion_card.tsx';
import {OccasionsGrid} from './components/occasions_grid.tsx';
import {Page} from '../../components/page/page.tsx';
import {FlexRow} from '../../components/utility/flex_row.tsx';
import {Alignment} from '../../enums/alignment.ts';
import {PageTitle} from '../../components/page/page_title.tsx';
import {__} from '../../support/lang/translations.ts';
import {PageContent} from '../../components/page/page_content.tsx';
import {OccasionPlaceholder} from './components/occasion_placeholder.tsx';
import {OccasionResults} from './components/occasion_results.tsx';
import {differenceInMinutes, parseISO} from 'date-fns';
import mainStyles from '../../components/main/main.module.scss';
import {SingleValue} from 'react-select';

const perPage = 4;

export function Occasions() {
    const dispatch = useAppDispatch();
    const occasionsData = useAppSelector((state) => state.occasions);
    const isLoading = useIsNavigatingOrPending((state) => state.occasions.status);
    const appConfig = useAppSelector((state) => state.appConfig.appconfig);

    const [searchParams, setSearchParams] = useSearchParams();

    const currentPage = Number.parseInt(searchParams.get('page') || '1', 10);
    const filters = useMemo<Filters>(() => {
        const brands = searchParams.getAll('brands');
        const models = searchParams.getAll('models');
        return {
            brands: brands.length > 0 ? brands : undefined,
            models: models.length > 0 ? models : undefined,
            transmission: searchParams.get('transmission') ?? undefined,
            buildYearFrom: parseIntOrFallback(searchParams.get('build_year_from'), undefined),
            buildYearTill: parseIntOrFallback(searchParams.get('build_year_till'), undefined),
            priceFrom: parseIntOrFallback(searchParams.get('price_from'), undefined),
            priceTill: parseIntOrFallback(searchParams.get('price_till'), undefined),
            mileageFrom: parseIntOrFallback(searchParams.get('mileage_from'), undefined),
            mileageTill: parseIntOrFallback(searchParams.get('mileage_till'), undefined),
            locationId: parseIntOrFallback(searchParams.get('location_id'), undefined),
        };
    }, [searchParams]);

    const filteredOccasions = (occasionsData.occasions && applyOccasionFilter(occasionsData.occasions, filters)) || [];
    const filteredOccasionCount = filteredOccasions.length;
    const filteredOccasionPage = filteredOccasions.slice(0, currentPage * perPage);

    useEffect(() => {
        dispatch(fetchOccasions());
        dispatch(loadOccasionsFromDatabase());
    }, [dispatch]);
    useEffect(() => {
        dispatch(filterOccasions(filters));
    }, [dispatch, filters]);

    const onSetPageCallback = useCallback(
        (value: SingleValue<number>) => {
            setSearchParams((current) => {
                if (value) {
                    current.set('page', value.toString());
                } else {
                    current.delete('page');
                }
                return current;
            });
        },
        [setSearchParams],
    );

    const occasions = filteredOccasionPage;
    const loadMorePossible = occasions.length < filteredOccasionCount;
    const lastOccasionFetch = occasionsData.lastOccasionFetch && parseISO(occasionsData.lastOccasionFetch);

    const onLoadMoreCallback = useCallback(() => {
        onSetPageCallback(currentPage + 1);
    }, [onSetPageCallback, currentPage]);

    return (
        <Main className={mainStyles.full}>
            <Page>
                <PageTitle>
                    <h1>
                        {__('occasions.index.titles.index')}
                        {appConfig?.appname}
                    </h1>
                </PageTitle>
                <PageContent>
                    <OccasionFilters filters={filters} isLoading={isLoading} />

                    <OccasionResults count={filteredOccasionCount} />
                </PageContent>

                {occasions?.length === 0 ||
                    (lastOccasionFetch && Math.abs(differenceInMinutes(lastOccasionFetch, new Date())) > 60 && (
                        <CenteredSpinner />
                    ))}

                <OccasionsGrid>
                    {filteredOccasionCount > 0 &&
                        filteredOccasionPage?.map((o, index) => (
                            <OccasionCard occasion={o} key={`${o?.id}-${index}`} />
                        ))}

                    {filteredOccasionCount === 0 && [1, 2, 3].map((i) => <OccasionPlaceholder key={i} />)}
                </OccasionsGrid>

                {loadMorePossible && (
                    <FlexRow alignment={Alignment.Center}>
                        <Button
                            disabled={!loadMorePossible || isLoading || occasionsData.status === Status.LOADING_MORE}
                            onClick={onLoadMoreCallback}
                            loading={occasionsData.status === Status.LOADING_MORE}
                        >
                            <i className="fa-light fa-refresh" />
                            <span>
                                {__(
                                    occasionsData.status === Status.LOADING_MORE
                                        ? 'occasions.index.buttons.loading'
                                        : 'occasions.index.buttons.more',
                                )}
                            </span>
                        </Button>
                    </FlexRow>
                )}
                <Outlet />
            </Page>
        </Main>
    );
}
